import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useLatestValue } from "@/hooks/useLatestValue";

export function Sponsors() {
  const [filters, setFilters] = useState({ q: "", town: "", county: "", route: "", rating: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [changes, setChanges] = useState(null);
  const [report, setReport] = useState(null);
  const filtersRef = useLatestValue(filters);

  const load = useCallback(async () => {
    const clean = Object.fromEntries(Object.entries(filtersRef.current).filter(([, v]) => v));
    setData((await api.get("/sponsors", { params: clean })).data);
  }, [filtersRef]);
  useEffect(() => { load(); api.get("/sponsors/changes/latest").then((r) => setChanges(r.data)); }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="sponsors-page">
      <p className="overline">Official source: GOV.UK register of licensed sponsors</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-semibold">UK licensed sponsor directory</h1>
      <p className="mt-3 max-w-3xl text-sm text-slate-600" data-testid="sponsor-directory-disclaimer">
        Holding a sponsor licence does not mean an organisation is currently recruiting or will sponsor a
        particular candidate. Organisations no longer present are described as “removed from the latest
        published register”, which does not mean a licence was revoked.
      </p>

      {changes?.version && (
        <div className="card p-5 mt-6" data-testid="register-version-panel">
          <p className="text-sm"><span className="font-semibold">Published register version:</span> {changes.version.file_name} ·
            {" "}{changes.version.record_count} organisations · published {new Date(changes.version.published_at).toLocaleDateString("en-GB")}</p>
          <p className="text-xs text-slate-500 mt-1">Checksum (SHA-256): {changes.version.checksum}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(changes.change_report?.counts || {}).map(([k, v]) => (
              <span key={k} className="pill bg-slate-100 text-slate-700">{k.replace(/_/g, " ")}: {v}</span>
            ))}
          </div>
          {changes.baseline_warning && (
            <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="baseline-warning">
              <strong>Comparison baseline: {changes.version.baseline?.file_name || "none"}
              {changes.version.baseline ? ` (${changes.version.baseline.record_count} records)` : ""}.</strong>{" "}
              {changes.baseline_warning}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            <a className="text-[#1d4ed8] underline" href={changes.version.official_source_url} target="_blank" rel="noreferrer">
              View the official GOV.UK register
            </a>
            {["new", "removed", "rating-changes", "route-changes"].map((r) => (
              <button key={r} data-testid={`report-${r}`} className="text-[#1d4ed8] underline"
                onClick={async () => setReport((await api.get(`/sponsors/reports/${r}`)).data)}>
                {r.replace(/-/g, " ")} report
              </button>
            ))}
            <a className="text-[#1d4ed8] underline" data-testid="export-sponsors"
              href={`${API}/sponsors/export?${new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)))}`}>
              Download filtered results (CSV)
            </a>
          </div>
        </div>
      )}

      {report && (
        <div className="card p-5 mt-4" data-testid="sponsor-report-panel">
          <h2 className="font-semibold capitalize">{report.report_type.replace(/-/g, " ")} report</h2>
          <p className="text-xs text-slate-500 mt-1">
            {report.items.length} entries shown{report.samples_capped_at ? ` (samples capped at ${report.samples_capped_at})` : ""}.
            {report.wording_note ? ` ${report.wording_note}` : ""}
          </p>
          <ul className="mt-3 text-sm space-y-1 max-h-64 overflow-y-auto">
            {report.items.slice(0, 200).map((i, n) => (
              <li key={n}>{i.organisation_name || i.to || i.from} {i.town ? `· ${i.town}` : ""} {i.from && i.to ? `· ${i.from} → ${i.to}` : ""}</li>
            ))}
          </ul>
          <button className="btn btn-ghost mt-3 text-xs py-1 px-3" onClick={() => setReport(null)}>Close</button>
        </div>
      )}

      <form className="mt-8 grid gap-3 md:grid-cols-5" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input data-testid="sponsor-search-input" className="field md:col-span-2" placeholder="Organisation name"
          value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <input data-testid="sponsor-town-input" className="field" placeholder="Town"
          value={filters.town} onChange={(e) => setFilters({ ...filters, town: e.target.value })} />
        <input data-testid="sponsor-route-input" className="field" placeholder="Route"
          value={filters.route} onChange={(e) => setFilters({ ...filters, route: e.target.value })} />
        <button data-testid="sponsor-search-submit" className="btn btn-primary justify-center">Search</button>
      </form>

      <p className="mt-6 text-sm text-slate-500" data-testid="sponsors-result-count">{data.total} organisations</p>
      <div className="mt-4 grid gap-3">
        {data.items.map((s) => (
          <Link key={s.id} to={`/sponsors/${s.id}`} data-testid={`sponsor-card-${s.id}`} className="card p-5 flex justify-between gap-4">
            <div>
              <h2 className="font-semibold">{s.organisation_name}</h2>
              <p className="text-sm text-slate-600">{s.town}{s.county ? `, ${s.county}` : ""}</p>
              <p className="text-xs text-slate-500 mt-1">Routes: {(s.routes || []).join(", ") || "Not stated"}</p>
            </div>
            <span className="pill bg-blue-50 text-blue-800 self-start">{s.rating || "Rating not stated"}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function SponsorDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [watched, setWatched] = useState(false);
  useEffect(() => { let active = true; setD(null); setError(""); api.get(`/sponsors/${id}`).then((r) => {if(active)setD(r.data);}).catch(() => {if(active)setError("Published organisation details could not be loaded. Please retry from the directory.");}); return () => {active=false;}; }, [id]);
  if (!d) return <div className="p-10 text-sm" role="status">{error || "Loading organisation…"}<Link className="block underline mt-3" to="/sponsors">Back to sponsor directory</Link></div>;
  const s = d.sponsor;
  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="sponsor-detail-page">
      <Link className="underline text-blue-800" to="/sponsors">Sponsor directory</Link>
      <h1 className="text-3xl font-semibold mt-4">{s.organisation_name}</h1>
      <p className="pill bg-blue-50 text-blue-800 mt-3">{d.present_in_current_register ? "Present in current published register" : d.present_in_current_register === false ? "Absent from current published register" : "Current presence unavailable"}</p>
      <p className="mt-2 text-slate-600">{s.town}{s.county ? `, ${s.county}` : ""} · {s.rating}</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <section className="card p-6">
            <h2 className="text-lg font-semibold">Licensed routes</h2>
            <ul className="mt-3 text-sm list-disc pl-5">{(s.routes || []).map((r) => <li key={r}>{r}</li>)}</ul>
            <p className="mt-4 text-xs text-slate-500">Companies House match: {s.companies_house_status || "Not checked"}{typeof s.match_confidence === "number" ? ` (confidence ${s.match_confidence}%)` : ""}. Organisations are never merged only because names are similar.</p>
          </section>
          <section className="card p-6">
            <h2 className="text-lg font-semibold">Sponsor register timeline</h2>
            <p className="text-xs text-slate-500 mt-3">{d.history_coverage?.note}</p>
            <p className="text-xs text-slate-500 mt-2">{d.history_coverage?.snapshots} published snapshots available{d.history_coverage?.truncated ? " (history capped at the latest 500)" : ""}. {d.history_coverage?.snapshots === 1 ? "Only one published snapshot is available, so earlier changes cannot be established." : ""}</p>
            <ol className="border-l-2 border-blue-100 pl-5 mt-5 space-y-5">
              {[...(d.timeline || [])].reverse().map((h,n) => <li key={`${h.version_id}-${n}`}><p className="font-semibold text-sm">{h.title}</p><p className="text-sm text-slate-600 mt-1">{h.details}</p><p className="text-xs text-slate-500 mt-2">Observed {h.at ? new Date(h.at).toLocaleDateString("en-GB") : "date unavailable"} · Snapshot {h.file_name || h.version_id}</p>{h.source_url && <a className="text-blue-800 underline text-xs" href={h.source_url} target="_blank" rel="noreferrer">Official register source</a>}</li>)}
            </ol>
          </section>
          <section className="card p-6">
            <h2 className="text-lg font-semibold">Vacancies on EuropEdu</h2>
            {d.open_vacancies.length === 0 ? <p className="mt-2 text-sm text-slate-600">No vacancies are currently advertised on EuropEdu by this organisation.</p>
              : <ul className="mt-2 text-sm space-y-1">{d.open_vacancies.map((j) => <li key={j.id}><Link className="text-[#1d4ed8] underline" to={`/jobs/${j.id}`}>{j.title}</Link></li>)}</ul>}
          </section>
        </div>
        <aside className="space-y-4">
          <div className="card p-6">
            <button data-testid="watch-sponsor-button" className="btn btn-primary w-full justify-center"
              onClick={async () => { try {await api.post("/sponsors/watchlist", { sponsor_id: id }); setWatched(true);setError("");}catch{setError("Sign in to add this organisation to your watchlist, then try again.");} }}>
              {watched ? "Added to watchlist" : "Add to watchlist"}
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="card p-6 text-xs text-slate-600" data-testid="sponsor-disclaimer">{d.disclaimer}</div>
        </aside>
      </div>
    </div>
  );
}

